import { Outlet, createFileRoute, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AngaMark } from "@/components/brand/AngaMark";
import { Progress } from "@/components/ui/progress";
import { apiFetch, getToken } from "@/lib/api";
import { usePlatformConfig } from "@/lib/config";
import {
  ONBOARDING_STEPS,
  isOnboardingStep,
  onboardingStepIndex,
  type OnboardingStep,
  useOnboardingStore,
} from "@/stores/onboarding";

export const Route = createFileRoute("/onboarding")({
  component: OnboardingLayout,
});

function stepFromPath(pathname: string): OnboardingStep {
  const segment = pathname.split("/").filter(Boolean).pop() || "welcome";
  if (isOnboardingStep(segment)) return segment;
  return "welcome";
}

function OnboardingLayout() {
  const { t } = useTranslation();
  const { platformName } = usePlatformConfig();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const setStep = useOnboardingStore((s) => s.setStep);

  const step = stepFromPath(pathname);
  const index = onboardingStepIndex(step);
  const total = ONBOARDING_STEPS.length;
  const progress = ((index + 1) / total) * 100;

  useEffect(() => {
    setStep(step);
    if (!getToken("user")) return;
    if (step === "welcome" || step === "carousel" || step === "signup" || step === "verify") return;
    void apiFetch("/user/onboarding", {
      method: "PATCH",
      audience: "user",
      body: JSON.stringify({ step }),
    }).catch(() => undefined);
  }, [setStep, step]);

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--primary-soft),_transparent_50%)]" />
      <div className="relative mx-auto flex min-h-screen max-w-lg flex-col px-5 py-6">
        <header className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#064E3B] text-white">
            <AngaMark className="h-6 w-6" title={platformName} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate font-display text-lg font-bold leading-none">{platformName}</p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              {t("onboarding.progress", { current: index + 1, total })}
            </p>
          </div>
        </header>
        <Progress value={progress} className="mb-8" />
        <main className="flex flex-1 flex-col">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
